<script lang="ts">
    import { onMount } from 'svelte';
    import { page } from '$app/stores';
    import get from 'lodash/get';
    import { getColor, getColorByLightness, personalizeText } from '$lib/helper';
    import { DEFAULT_LAYOUT } from '$lib/components/constants';
    import Uploader from '$lib/global/helper/Uploader';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { trackCalendly, trackFormSubmit } from '$lib/global/tracking';

    // Types
    import type { ComponentType } from 'svelte';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { Component } from '$lib/types/Components/SharedTypes';
    import type { FormValues } from '$lib/types/Form';
    import type { Field } from '$lib/types/Field';
    import type { TrackingProperty } from '$lib/types/TrackingProperty';
    import type { Language } from '$lib/types/Language';
    import type { FormQuestionContent } from '$lib/types/Components/FormQuestion/FormQuestionContent';
    import type { InputData } from '$lib/types/Components/FormQuestion/InputData';

    export const id = '';
    export let content: FormQuestionContent;
    export let childComponents: Component[] = [];
    export let fields: Field[] = [];
    export let theme: Theme;
    export let nextPage = '';
    export let campaignId = '';
    export let companyId = '';
    export let firstResultPage = '';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'none';
    export let trackingProperties: TrackingProperty[] = [];
    export let language: Language = 'de';
    export let layout = DEFAULT_LAYOUT;

    const {
        data: { inputMap, pageIdSlugMap },
    } = $page;

    const form = childComponents[0];
    const formType = form.componentType;
    const inputs: InputData[] = [];

    // File Upload
    let fileToUpload: File;
    let fileUploading = false;
    let uploadedFileUrl = '';
    let fileInputFieldName = '';
    const uploader = new Uploader(companyId, campaignId);

    // Button
    const button = form.components.find(
        (component: Component) => component.componentType === 'button',
    );
    const btnBgColor = getColor(
        get(button, 'content.backgroundColor'),
        theme.buttonBackgroundColor,
    );
    const btnColor = getColor(get(button, 'content.color'), getColorByLightness(btnBgColor));

    // Navigation
    const externalLink = get(button, 'content.linking.url');
    const nextPageNavigator = new NextPageNavigator(campaignId, firstResultPage, pageIdSlugMap);
    const isExternal = !get(button, 'content.linking.isInternal', true) && !!externalLink;
    const regularNextPage = get(button, 'content.linking.pageId', nextPage);

    let FormComponent: ComponentType;

    const personalizedText = personalizeText(
        content.wysiwyg,
        trackingProperties,
        campaignId,
        language,
    );

    const personalizedButtonText = personalizeText(
        button?.content.wysiwyg,
        trackingProperties,
        campaignId,
        language,
    );

    onMount(async () => {
        // Dynamic import
        if (formType === 'formURL') {
            FormComponent = (await import('$lib/components/_DisplayComponents/CalendlyForm.svelte'))
                .default;
        } else {
            FormComponent = (
                await import('$lib/components/_DisplayComponents/FormQuestion/FormQuestion.svelte')
            ).default;
        }
    });

    const handleFileAccept = (file: File) => {
        fileToUpload = file;
    };

    form.components.forEach((component: Component) => {
        if (component.componentType === 'input') {
            const inputData: InputData = {
                type: component.content.inputType,
                name: get(
                    inputMap,
                    `byComponent[${component._id}].name`,
                    component.content.fieldName,
                ),
                emoji: component.content.icon,
                dateFormat: component.content.dateFormat,
                customOptions: component.content.customOptions,
                showEmoji: component.content.showIcon,
                required: component.content.required,
                conversionTrigger: component.content.conversionTrigger,
                placeholder: component.content.placeholder,
                wysiwyg: get(component, 'content.misc.wysiwyg', {}),
                autocomplete: get(inputMap, `byComponent[${component._id}].autocomplete`, ''),
                onFileAccept: undefined as unknown as (file: File) => void,
            };

            if (component.content.inputType === 'file') {
                inputData.onFileAccept = handleFileAccept;
                fileInputFieldName = inputData.name;
            }

            inputs.push(inputData);
        }
    });

    const uploadFile = async () => {
        fileUploading = true;

        try {
            uploadedFileUrl = await uploader.upload(fileToUpload);
            fileUploading = false;
        } catch (err) {
            fileUploading = false;
            console.warn('upload failed:', err);
        }
    };

    const trackSubmit = (values: FormValues) => {
        trackFormSubmit({ form, button, values, fields, inputMap });
    };

    const uploadFileIfNecessary = async (values: FormValues) => {
        if (values[fileInputFieldName]) {
            await uploadFile();

            values[fileInputFieldName] = uploadedFileUrl;
        }
    };

    const sanitizeValues = (values: FormValues) => {
        // add protocol to website url if necessary
        if (values['website']) {
            let url = values['website'] as string;

            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                url = 'https://' + url;
            }

            values['website'] = url;
        }

        return values;
    };

    const handleCalendlySubmit = async (values: FormValues) => {
        await uploadFileIfNecessary(values);
        const sanitizedValues = sanitizeValues(values);

        trackSubmit(sanitizedValues);
    };

    // Fired after Calendly Submit
    const handleSubmit = async (values: FormValues) => {
        await uploadFileIfNecessary(values);
        const sanitizedValues = sanitizeValues(values);

        if (formType === 'formURL') {
            // Track Calendly booking event; formData has already been tracked (handleCalendlyFormSubmit)
            trackCalendly();
        } else {
            trackSubmit(sanitizedValues);
        }

        // Navigate to external page
        if (isExternal) {
            return nextPageNavigator.goExternal(externalLink);
        }

        return nextPageNavigator.go(regularNextPage);
    };

    const handleMouseEnter = () => {
        nextPageNavigator.preload(regularNextPage, isExternal);
    };
</script>

<svelte:component
    this={FormComponent}
    draft={personalizedText}
    color={getColor(content.color, theme.fontColor)}
    align={content.align}
    onSubmit={handleSubmit}
    onCalendlyFormSubmit={handleCalendlySubmit}
    onMouseEnter={handleMouseEnter}
    box={content.box}
    btnId={button?._id}
    btnDraft={personalizedButtonText}
    btnSize={button?.content.size}
    btnAlign={button?.content.align}
    btnAnimation={get(button, 'content.animation', '')}
    calendlyUrl={form.content.url}
    disabled={fileUploading}
    {fileUploading}
    {btnBgColor}
    {btnColor}
    {inputs}
    {transitionDelay}
    {transitionEffect}
    {layout}
    {language}
/>
